import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const reasonLabels = {
  PURCHASE_RETURN: "Purchase Return",
  PRICE_CORRECTION: "Price Correction",
  DAMAGED_GOODS: "Damaged Goods",
  OTHER: "Other",
};

const emptyForm = { purchaseInvoiceId: "", reason: "PURCHASE_RETURN", noteDate: "", remarks: "" };

const PurchaseDebitNotes = () => {
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [saving, setSaving] = useState(false);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/purchase-debit-notes");
      setNotes(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load purchase debit notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    api
      .get("/api/v1/purchase-invoices", { size: 200, status: "VERIFIED" })
      .then((data) => setInvoices(data?.content || []))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setFormData(emptyForm);
    setInvoiceItems([]);
    setSelectedQuantities({});
    setShowModal(true);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "purchaseInvoiceId") {
      setSelectedQuantities({});
      if (!value) {
        setInvoiceItems([]);
        return;
      }
      try {
        const invoice = await api.get(`/api/v1/purchase-invoices/${value}`);
        setInvoiceItems(invoice?.items || []);
      } catch (error) {
        toast.error(error.message || "Failed to load invoice items");
      }
    }
  };

  const handleQuantityChange = (itemId, value) => {
    setSelectedQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = Object.entries(selectedQuantities)
      .filter(([, qty]) => qty !== "" && Number(qty) > 0)
      .map(([purchaseInvoiceItemId, qty]) => ({ purchaseInvoiceItemId: Number(purchaseInvoiceItemId), quantity: Number(qty) }));
    if (items.length === 0) {
      toast.error("Enter a quantity for at least one item");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/v1/purchase-debit-notes", {
        purchaseInvoiceId: Number(formData.purchaseInvoiceId),
        reason: formData.reason,
        noteDate: formData.noteDate || undefined,
        remarks: formData.remarks || undefined,
        items,
      });
      toast.success("Purchase debit note created");
      setShowModal(false);
      loadNotes();
    } catch (error) {
      toast.error(error.message || "Failed to create purchase debit note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Purchase debit notes reduce what you owe a brand against a purchase invoice — for purchase returns, price
        corrections, or damaged goods. A "Purchase Return" debit note also pulls the returned quantity back out of stock.
      </div>

      <div className="filters-bar">
        <button className="primary" onClick={openCreate} style={{ marginLeft: "auto" }}>
          + New Purchase Debit Note
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Debit Note #</th>
              <th>Date</th>
              <th>Invoice #</th>
              <th>Brand</th>
              <th>Reason</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {notes.length === 0 ? (
              <tr>
                <td colSpan={6}>No purchase debit notes found.</td>
              </tr>
            ) : (
              notes.map((note) => (
                <tr key={note.id}>
                  <td>{note.debitNoteNumber}</td>
                  <td>{note.noteDate}</td>
                  <td>{note.brandInvoiceNumber}</td>
                  <td>{note.brandName}</td>
                  <td>{reasonLabels[note.reason] || note.reason}</td>
                  <td>{money(note.totalAmount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "760px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Create Purchase Debit Note</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Purchase Invoice</label>
                  <select name="purchaseInvoiceId" value={formData.purchaseInvoiceId} onChange={handleChange} required>
                    <option value="">Select invoice</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.brandInvoiceNumber}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <select name="reason" value={formData.reason} onChange={handleChange}>
                    <option value="PURCHASE_RETURN">Purchase Return</option>
                    <option value="PRICE_CORRECTION">Price Correction</option>
                    <option value="DAMAGED_GOODS">Damaged Goods</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="noteDate" value={formData.noteDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input name="remarks" value={formData.remarks} onChange={handleChange} />
                </div>
              </div>

              {invoiceItems.length > 0 ? (
                <>
                  <h4 style={{ marginTop: "20px", marginBottom: "8px", fontWeight: 600 }}>Items to debit</h4>
                  <div className="table-wrapper" style={{ marginTop: 0 }}>
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Invoiced Qty</th>
                          <th>Unit Price</th>
                          <th>Qty to Debit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.productName || item.productId}</td>
                            <td>{item.quantity}</td>
                            <td>{money(item.unitPrice)}</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                max={item.quantity}
                                step="0.001"
                                style={{ width: "100px" }}
                                value={selectedQuantities[item.id] ?? ""}
                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : formData.purchaseInvoiceId ? (
                <div className="empty-state" style={{ marginTop: "12px" }}>
                  <span className="empty-state-title">No items on this invoice</span>
                </div>
              ) : null}

              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PurchaseDebitNotes;
